import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Camera,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Disc,
  FileCheck2,
  FileSpreadsheet,
  FileText,
  Loader2,
  Plus,
  Receipt,
  RefreshCw,
  Search,
  Tag,
} from 'lucide-react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { tyreManagementApi, unwrapList } from '../api/tyreManagementApi';
import useAsyncData from '../hooks/useAsyncData';
import { tyreQueryKeys } from '../api/queryKeys';
import useTyreMutation from '../hooks/useTyreMutation';
import ModuleHeader from '../components/ModuleHeader';
import EmptyState from '../components/EmptyState';
import StateBadge from '../components/StateBadge';
import FormField from '../components/FormField';
import FileUpload from '../components/FileUpload';
import BulkTyreUploadDialog from '../components/BulkTyreUploadDialog';
import SelectControl from '../components/SelectControl';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { formatCurrency } from '../lib/utils';
import { TyreDetailSheet } from '../components/TyreDetailLifecycle';
import { TableSkeleton } from '../components/TmSkeletonLayouts';
import { Skeleton } from '../components/ui/skeleton';

const emptyForm = {
  tyre_id: '',
  site: '',
  serial_number: '',
  brand: '',
  model: '',
  size: '',
  tyre_type: '',
  supplier: '',
  manufacturing_date: '',
  purchase_date: '',
  purchase_cost: '',
  initial_tread_depth_mm: '',
  current_tread_depth_mm: '',
  warranty: '',
  batch_number: '',
  invoice_number: '',
  remarks: '',
};

const requiredInvoiceCompletionFields = {
  serial_number: 'Manufacturer serial',
  brand: 'Brand',
  model: 'Model',
  size: 'Size',
  tyre_type: 'Tyre type',
  supplier: 'Supplier',
  manufacturing_date: 'Manufacturing date',
  purchase_date: 'Purchase date',
  initial_tread_depth_mm: 'Initial tread depth',
  warranty: 'Warranty',
};

const TyreInventoryPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { token } = useParams();
  const [search, setSearch] = useState(() => new URLSearchParams(location.search).get('search') || '');
  const [custody, setCustody] = useState(() => new URLSearchParams(location.search).get('custody') || '');
  const [condition, setCondition] = useState(() => new URLSearchParams(location.search).get('condition') || '');
  const [page, setPage] = useState(() => Math.max(1, Number(new URLSearchParams(location.search).get('page')) || 1));
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTyreId, setSelectedTyreId] = useState(
    () => new URLSearchParams(location.search).get('tyre') || ''
  );
  const [form, setForm] = useState(emptyForm);
  const [photo, setPhoto] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [extractingInvoice, setExtractingInvoice] = useState(false);
  const [invoiceExtraction, setInvoiceExtraction] = useState(null);
  const [invoiceError, setInvoiceError] = useState('');
  const [extractedFields, setExtractedFields] = useState([]);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [attachmentWarning, setAttachmentWarning] = useState('');
  const extractInvoiceMutation = useTyreMutation(tyreManagementApi.extractPurchaseInvoice, { invalidate: false });
  const createTyreMutation = useTyreMutation(tyreManagementApi.createTyre);
  const uploadAttachmentMutation = useTyreMutation(
    ({ tyreId, body }) => tyreManagementApi.uploadTyreAttachment(tyreId, body)
  );

  const inventoryState = useAsyncData(
    tyreQueryKeys.tyres({ search, custody, condition, page, page_size: 12 }),
    () => tyreManagementApi.getTyres({ search, custody, condition, page, page_size: 12 }),
  );
  const sitesState = useAsyncData(tyreQueryKeys.sites(), tyreManagementApi.getSites);
  const detailState = useAsyncData(tyreQueryKeys.tyre(selectedTyreId), () => tyreManagementApi.getTyre(selectedTyreId), Boolean(selectedTyreId));
  const historyState = useAsyncData(tyreQueryKeys.tyreHistory(selectedTyreId), () => tyreManagementApi.getTyreHistory(selectedTyreId), Boolean(selectedTyreId));
  const lookupState = useAsyncData(tyreQueryKeys.tyreLookup(token), () => tyreManagementApi.getTyreByToken(token), Boolean(token));

  useEffect(() => {
    if (!token) {
      const params = new URLSearchParams(location.search);
      setSelectedTyreId(params.get('tyre') || '');
      setSearch(params.get('search') || '');
      setCustody(params.get('custody') || '');
      setCondition(params.get('condition') || '');
      setPage(Math.max(1, Number(params.get('page')) || 1));
    }
  }, [location.search, token]);

  const updateListQuery = (changes) => {
    const params = new URLSearchParams(location.search);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined || (key === 'page' && value === 1)) {
        params.delete(key);
      } else {
        params.set(key, String(value));
      }
    });
    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
    });
  };

  const updateTyreQuery = (tyreId, replace = false) => {
    const params = new URLSearchParams(location.search);
    if (tyreId) params.set('tyre', tyreId);
    else params.delete('tyre');
    const nextLocation = {
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
    };
    if (replace) history.replace(nextLocation);
    else history.push(nextLocation);
  };

  const openTyre = (tyreId, { replace = false, keepWarning = false } = {}) => {
    if (!keepWarning) setAttachmentWarning('');
    setSelectedTyreId(tyreId);
    updateTyreQuery(tyreId, replace);
  };

  const closeTyre = () => {
    setSelectedTyreId('');
    setAttachmentWarning('');
    updateTyreQuery('', true);
  };

  useEffect(() => {
    if (lookupState.data?.id) openTyre(lookupState.data.id, { replace: true });
  }, [lookupState.data]);

  const tyres = unwrapList(inventoryState.data);
  const totalCount = inventoryState.data?.count || tyres.length;
  const sites = useMemo(() => unwrapList(sitesState.data), [sitesState.data]);

  useEffect(() => {
    if (sites.length === 1 && form.site !== String(sites[0].id)) {
      setForm((current) => ({ ...current, site: String(sites[0].id) }));
    }
  }, [form.site, sites]);

  const handleForm = (event) => setForm({ ...form, [event.target.name]: event.target.value });

  const resetCreateForm = () => {
    setForm(emptyForm);
    setPhoto(null);
    setInvoice(null);
    setInvoiceExtraction(null);
    setInvoiceError('');
    setExtractedFields([]);
    setFormError('');
  };

  const handleInvoice = async (file) => {
    setInvoice(file);
    setInvoiceExtraction(null);
    setInvoiceError('');
    setExtractedFields([]);
    if (!file) return;

    setExtractingInvoice(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const result = await extractInvoiceMutation.mutateAsync(body);
      const suggestions = Object.entries(result.fields || {}).reduce((values, [name, suggestion]) => ({
        ...values,
        [name]: suggestion.value,
      }), {});
      setForm((current) => ({ ...current, ...suggestions }));
      setExtractedFields(Object.keys(suggestions));
      setInvoiceExtraction(result);
    } catch (requestError) {
      setInvoiceError(`${requestError.message} Enter the missing details manually.`);
    } finally {
      setExtractingInvoice(false);
    }
  };

  const createTyre = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((key) => {
        if (payload[key] === '') delete payload[key];
      });
      if (!payload.site) {
        setFormError(sites.length
          ? 'Select an inventory site before creating a tyre.'
          : 'No inventory sites are available for your account.');
        return;
      }
      const created = await createTyreMutation.mutateAsync(payload);
      const uploads = [
        invoice && { file: invoice, category: 'purchase_invoice', label: 'invoice' },
        photo && { file: photo, category: 'profile', label: 'photo' },
      ].filter(Boolean);
      const uploadResults = await Promise.allSettled(uploads.map(({ file, category }) => {
        const body = new FormData();
        body.append('file', file);
        body.append('category', category);
        return uploadAttachmentMutation.mutateAsync({ tyreId: created.id, body });
      }));
      const failedUploads = uploadResults.reduce((labels, result, index) => (
        result.status === 'rejected' ? [...labels, uploads[index].label] : labels
      ), []);
      if (failedUploads.length) {
        setAttachmentWarning(
          `Tyre created, but the ${failedUploads.join(' and ')} could not be attached. The tyre record is saved; add the missing ${failedUploads.length === 1 ? 'attachment' : 'attachments'} later.`
        );
      }
      setCreateOpen(false);
      resetCreateForm();
      openTyre(created.id, { replace: true, keepWarning: true });
    } catch (requestError) {
      setFormError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const missingFields = Object.entries(requiredInvoiceCompletionFields)
    .filter(([name]) => !form[name])
    .map(([, label]) => label);
  const extractedClass = (name) => (extractedFields.includes(name) ? 'border-emerald-300 bg-emerald-50/40' : '');

  return (
    <div className="space-y-6">
      <ModuleHeader
        eyebrow="Tyre register"
        title="Tyres & inventory"
        description="Search tyre records by ID, site, custody, or condition."
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Add single tyre
            </Button>
            <Button onClick={() => setBulkUploadOpen(true)}>
              <FileSpreadsheet className="h-4 w-4" /> Bulk upload
            </Button>
          </div>
        )}
      />

      <Card>
        <CardHeader className="flex-col gap-4 space-y-0 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle>Tyre inventory</CardTitle>
            <p className="mt-1 text-sm text-slate-500">
              {inventoryState.loading ? <Skeleton height={14} width={120} /> : `${totalCount} tyre records`}
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center lg:w-auto">
            <div className="relative min-w-[260px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                  updateListQuery({ search: event.target.value, page: 1 });
                }}
                className="pl-9"
                placeholder="Search by ID, serial, brand, or model"
                disabled={inventoryState.loading}
              />
            </div>
            <SelectControl
              className="min-w-[180px]"
              value={custody}
              onChange={(event) => {
                setCustody(event.target.value);
                setPage(1);
                updateListQuery({ custody: event.target.value, page: 1 });
              }}
              disabled={inventoryState.loading}
            >
              <option value="">All custody states</option>
              <option value="available">Available</option>
              <option value="fitted">Fitted</option>
              <option value="retread_vendor">With re-treading vendor</option>
              <option value="scrapped">Scrapped</option>
              <option value="sold">Sold</option>
              <option value="lost">Lost</option>
            </SelectControl>
            <SelectControl
              className="min-w-[160px]"
              value={condition}
              onChange={(event) => {
                setCondition(event.target.value);
                setPage(1);
                updateListQuery({ condition: event.target.value, page: 1 });
              }}
              disabled={inventoryState.loading}
            >
              <option value="">All conditions</option>
              <option value="new">New</option>
              <option value="serviceable">Serviceable</option>
              <option value="retreaded">Re-treaded</option>
              <option value="worn">Worn</option>
              <option value="damaged">Damaged</option>
              <option value="failed">Failed</option>
              <option value="unknown">Unknown</option>
            </SelectControl>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {inventoryState.error && (
            <div className="px-6 pb-6">
              <EmptyState title="Inventory unavailable" description={inventoryState.error} />
            </div>
          )}
          {inventoryState.loading && !inventoryState.error && <TableSkeleton rows={6} columns={7} />}
          {!inventoryState.loading && !inventoryState.error && !tyres.length && (
            <div className="px-6 pb-6">
              <EmptyState
                title="No tyres found"
                description="Upload an Excel inventory or change your search and filters."
                action={<Button onClick={() => setBulkUploadOpen(true)}><FileSpreadsheet className="h-4 w-4" /> Bulk upload tyres</Button>}
              />
            </div>
          )}
          {tyres.length > 0 && !inventoryState.loading && (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tyre</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Custody</TableHead>
                    <TableHead>Condition</TableHead>
                    <TableHead>Vehicle / position</TableHead>
                    <TableHead>Lifecycle cost</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tyres.map((tyre) => (
                    <TableRow key={tyre.id}>
                      <TableCell>
                        <button
                          type="button"
                          className="tm-plain-button"
                          onClick={() => openTyre(tyre.id)}
                        >
                          <p className="font-extrabold text-emerald-700 hover:underline">{tyre.tyre_id}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            {tyre.brand} {tyre.model} · {tyre.size}
                          </p>
                        </button>
                      </TableCell>
                      <TableCell>{tyre.site_name}</TableCell>
                      <TableCell><StateBadge value={tyre.custody} /></TableCell>
                      <TableCell><StateBadge value={tyre.condition} /></TableCell>
                      <TableCell>
                        <p className="font-semibold text-slate-800">{tyre.current_vehicle || '—'}</p>
                        <p className="text-xs text-slate-500">{tyre.current_position || 'Not fitted'}</p>
                      </TableCell>
                      <TableCell className="font-semibold">{formatCurrency(tyre.lifetime_cost)}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => openTyre(tyre.id)}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4">
                <p className="text-sm text-slate-500">Page {page}</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page === 1}
                    onClick={() => {
                      setPage(page - 1);
                      updateListQuery({ page: page - 1 });
                    }}
                  >
                    <ChevronLeft className="h-4 w-4" /> Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!inventoryState.data?.next}
                    onClick={() => {
                      setPage(page + 1);
                      updateListQuery({ page: page + 1 });
                    }}
                  >
                    Next <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <BulkTyreUploadDialog
        open={bulkUploadOpen}
        onOpenChange={setBulkUploadOpen}
        sites={sites}
        onComplete={() => Promise.resolve()}
      />

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open && !submitting) resetCreateForm();
        }}
      >
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-4xl lg:max-w-5xl">
          <DialogHeader>
            <div className="flex items-center gap-3.5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-emerald-200/80 bg-emerald-50 text-emerald-700 shadow-sm">
                <Disc className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle>Add tyre</DialogTitle>
                <DialogDescription>
                  Add the tyre record. Leave Tyre ID blank to generate it automatically.
                  {sites.length === 1 && ` It will be added to ${sites[0].name}.`}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <form onSubmit={createTyre} className="flex min-h-0 min-w-0 flex-1 flex-col">
            <DialogBody className="min-w-0 space-y-7">
              <div className="overflow-hidden rounded-xl border border-sky-200 bg-gradient-to-r from-sky-50 via-white to-emerald-50/60">
                <div className="grid gap-5 p-5 md:grid-cols-[minmax(0,1fr)_minmax(18rem,0.8fr)] md:items-center">
                  <div>
                    <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-sky-700">
                      <FileText className="h-4 w-4" /> Invoice import
                    </div>
                    <h3 className="mb-0 mt-2 text-lg font-extrabold tracking-tight text-slate-950">Upload a purchase invoice</h3>
                    <p className="mb-0 mt-1 text-sm leading-6 text-slate-600">
                      Optional. We’ll fill what we can; review the fields and add anything missing.
                    </p>
                    {extractingInvoice && (
                      <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-sky-700" role="status">
                        <Loader2 className="h-4 w-4 animate-spin" /> Extracting invoice details…
                      </div>
                    )}
                    {!extractingInvoice && invoiceExtraction && (
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 font-bold text-emerald-800">
                          <FileCheck2 className="h-3.5 w-3.5" /> {extractedFields.length} fields found
                        </span>
                        <span className={`rounded-full px-2.5 py-1 font-bold ${missingFields.length ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                          {missingFields.length ? `${missingFields.length} required fields missing` : 'All required fields complete'}
                        </span>
                      </div>
                    )}
                  </div>
                  <FileUpload
                    id="purchase-invoice"
                    file={invoice}
                    onChange={handleInvoice}
                    accept="application/pdf,image/jpeg,image/png"
                    hint="PDF, JPEG, or PNG up to 10 MB"
                  />
                </div>
                {(invoiceError || invoiceExtraction?.warnings?.length > 0) && (
                  <div className={`border-t px-5 py-3 text-xs leading-5 ${invoiceError ? 'border-red-200 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                    {invoiceError || invoiceExtraction.warnings.join(' ')}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-50 text-emerald-700 border border-emerald-100/80">
                    <Tag className="h-3.5 w-3.5" />
                  </span>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                    Tyre details
                  </h3>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {sites.length > 1 && (
                    <FormField
                      label="Inventory site"
                      htmlFor="tyre-site"
                      hint="Choose where this tyre will be stored before it is fitted."
                      required
                    >
                      <SelectControl id="tyre-site" name="site" value={form.site} onChange={handleForm} required searchable searchPlaceholder="Search sites…">
                        <option value="">Select inventory site</option>
                        {sites.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.name}
                          </option>
                        ))}
                      </SelectControl>
                    </FormField>
                  )}

                  <FormField label="Manufacturer serial" htmlFor="serial" required>
                    <Input
                      id="serial"
                      name="serial_number"
                      value={form.serial_number}
                      onChange={handleForm}
                      className={extractedClass('serial_number')}
                      placeholder="e.g. SN-8839201"
                      required
                    />
                  </FormField>

                  <FormField label="Tyre ID" htmlFor="tyre-id" optional>
                    <Input
                      id="tyre-id"
                      name="tyre_id"
                      value={form.tyre_id}
                      onChange={handleForm}
                      placeholder="Leave blank to generate"
                    />
                  </FormField>

                  <FormField label="Brand" htmlFor="brand" required>
                    <Input
                      id="brand"
                      name="brand"
                      value={form.brand}
                      onChange={handleForm}
                      className={extractedClass('brand')}
                      placeholder="e.g. Michelin, Apollo, MRF"
                      required
                    />
                  </FormField>

                  <FormField label="Model" htmlFor="model" required>
                    <Input
                      id="model"
                      name="model"
                      value={form.model}
                      onChange={handleForm}
                      className={extractedClass('model')}
                      placeholder="e.g. X Multi Energy, EnduRace"
                      required
                    />
                  </FormField>

                  <FormField label="Size" htmlFor="size" required>
                    <Input
                      id="size"
                      name="size"
                      value={form.size}
                      onChange={handleForm}
                      className={extractedClass('size')}
                      placeholder="e.g. 10.00 R20, 295/80 R22.5"
                      required
                    />
                  </FormField>

                  <FormField label="Tyre type" htmlFor="tyre-type" required className="sm:col-span-2 lg:col-span-1">
                    <SelectControl
                      id="tyre-type"
                      name="tyre_type"
                      value={form.tyre_type}
                      onChange={handleForm}
                      className={extractedClass('tyre_type')}
                      required
                    >
                      <option value="">Select tyre type</option>
                      <option value="Radial">Radial</option>
                      <option value="Radial (Tubeless)">Radial (Tubeless)</option>
                      <option value="Radial (Tube-type)">Radial (Tube-type)</option>
                      <option value="Bias / Cross-ply">Bias / Cross-ply</option>
                      <option value="Solid / Industrial">Solid / Industrial</option>
                    </SelectControl>
                  </FormField>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-50 text-emerald-700 border border-emerald-100/80">
                    <Receipt className="h-3.5 w-3.5" />
                  </span>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                    Purchase details
                  </h3>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <FormField label="Supplier" htmlFor="supplier" required>
                    <Input
                      id="supplier"
                      name="supplier"
                      value={form.supplier}
                      onChange={handleForm}
                      className={extractedClass('supplier')}
                      placeholder="e.g. Direct OEM / Metro Tyres"
                      required
                    />
                  </FormField>

                  <FormField label="Purchase cost" htmlFor="purchase-cost" optional>
                    <Input
                      id="purchase-cost"
                      name="purchase_cost"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={form.purchase_cost}
                      onChange={handleForm}
                      className={extractedClass('purchase_cost')}
                    />
                  </FormField>

                  <FormField label="Initial tread depth (mm)" htmlFor="tread" required>
                    <Input
                      id="tread"
                      name="initial_tread_depth_mm"
                      type="number"
                      min="0"
                      step="0.1"
                      placeholder="e.g. 15.0"
                      value={form.initial_tread_depth_mm}
                      onChange={handleForm}
                      required
                    />
                  </FormField>

                  <FormField label="Manufacturing date" htmlFor="manufacturing-date" required>
                    <Input
                      id="manufacturing-date"
                      name="manufacturing_date"
                      type="date"
                      value={form.manufacturing_date}
                      onChange={handleForm}
                      className={extractedClass('manufacturing_date')}
                      required
                    />
                  </FormField>

                  <FormField label="Purchase date" htmlFor="purchase-date" required>
                    <Input
                      id="purchase-date"
                      name="purchase_date"
                      type="date"
                      value={form.purchase_date}
                      onChange={handleForm}
                      className={extractedClass('purchase_date')}
                      required
                    />
                  </FormField>

                  <FormField label="Warranty" htmlFor="warranty" required>
                    <Input
                      id="warranty"
                      name="warranty"
                      value={form.warranty}
                      onChange={handleForm}
                      className={extractedClass('warranty')}
                      placeholder="e.g. 3 years / 100,000 km"
                      required
                    />
                  </FormField>

                  <FormField label="Invoice number" htmlFor="invoice" optional>
                    <Input
                      id="invoice"
                      name="invoice_number"
                      value={form.invoice_number}
                      onChange={handleForm}
                      className={extractedClass('invoice_number')}
                      placeholder="e.g. INV-2026-0041"
                    />
                  </FormField>

                  <FormField label="Batch number" htmlFor="batch" optional>
                    <Input
                      id="batch"
                      name="batch_number"
                      value={form.batch_number}
                      onChange={handleForm}
                      className={extractedClass('batch_number')}
                      placeholder="e.g. BATCH-26A"
                    />
                  </FormField>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-50 text-emerald-700 border border-emerald-100/80">
                    <Camera className="h-3.5 w-3.5" />
                  </span>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                    Photos & notes
                  </h3>
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <FormField
                    label="Tyre profile photo"
                    htmlFor="profile-photo"
                    optional
                    hint="Use for initial identification. Add removal or replacement photos during those actions."
                  >
                    <FileUpload
                      id="profile-photo"
                      file={photo}
                      onChange={setPhoto}
                      hint="JPEG, PNG, or WebP up to 10 MB"
                    />
                  </FormField>

                  <FormField label="Notes" htmlFor="remarks" optional>
                    <Textarea
                      id="remarks"
                      name="remarks"
                      value={form.remarks}
                      onChange={handleForm}
                      placeholder="Add inspection or supplier notes."
                      className="min-h-[120px] resize-y"
                    />
                  </FormField>
                </div>
              </div>

              {formError && (
                <div className="flex items-center gap-2.5 rounded-xl border border-red-200 bg-red-50/90 p-4 text-sm text-red-700 shadow-sm">
                  <AlertCircle className="h-5 w-5 shrink-0 text-red-600" />
                  <p className="font-medium">{formError}</p>
                </div>
              )}
            </DialogBody>

            <DialogFooter>
              <div className="flex w-full items-center justify-between">
                <p className="text-xs text-slate-500 hidden sm:flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-emerald-500 inline-block" />
                  New tyres start as <strong className="font-semibold text-slate-700">Available</strong>
                </p>
                <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setCreateOpen(false);
                      resetCreateForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )}
                    {submitting ? 'Creating tyre…' : 'Create tyre'}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {lookupState.error && <EmptyState title="QR lookup unavailable" description={lookupState.error} />}

      <Dialog open={Boolean(selectedTyreId)} onOpenChange={(open) => !open && closeTyre()}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-4xl lg:max-w-5xl">
          <DialogTitle className="sr-only">{detailState.data?.tyre_id || 'Tyre details'}</DialogTitle>
          <DialogDescription className="sr-only">
            {detailState.data
              ? `${detailState.data.brand} ${detailState.data.model} · ${detailState.data.size} · ${detailState.data.site_name}`
              : 'Loading tyre details'}
          </DialogDescription>

          {attachmentWarning && (
            <div className="mx-6 mt-5 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" role="alert">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <p className="font-medium">{attachmentWarning}</p>
            </div>
          )}
          {detailState.loading && (
            <DialogBody className="space-y-4" aria-live="polite">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Loader2 className="h-4 w-4 animate-spin" />Loading tyre details…</div>
              <Skeleton height={180} />
              <Skeleton height={120} />
            </DialogBody>
          )}
          {!detailState.loading && detailState.data && (
            <DialogBody className="min-w-0">
              <TyreDetailSheet
                tyre={detailState.data}
                events={historyState.data || []}
                eventsLoading={historyState.loading}
              />
            </DialogBody>
          )}
          {!detailState.loading && detailState.error && (
            <DialogBody>
              <EmptyState
                title="Tyre details unavailable"
                description={detailState.error}
                action={<Button onClick={detailState.reload}>Retry</Button>}
              />
            </DialogBody>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeTyre}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TyreInventoryPage;
